/**
 * Playing the app without a microphone.
 *
 * Every screen that has to tell the player something is unavailable ends with this, and
 * that is the point: microphone denied, browser too old, HTTP instead of HTTPS — the
 * answer is never "come back with a different device". The graph, the suggestions, the
 * history and the sketchbook all work perfectly well on typed chords. Listening is how
 * the app is *best* used, not the only way it can be used.
 */

import { useCallback, useState } from 'react';
import type { CanonicalChord } from '@hc/shared';
import { performanceNow } from '../../state/chordInput.js';
import { ChordTextEntry } from './ChordTextEntry.js';
import { synthesizeDetection } from './chordReading.js';
import type { SessionView } from './sessionView.js';

/**
 * Commit a typed chord to the session.
 *
 * Uses the store's `onManualChord` when it has one — the live host always supplies it, so
 * this is the path that actually runs. The dispatch fallback exists only for a view with
 * no host at all, and it must mirror `commitChordSelection` exactly: the timestamp comes
 * from `performanceNow`, because the reducer's arpeggio-window arithmetic compares against
 * recognizer timestamps and mixed clock bases produce negative deltas that pass the
 * bound; the source is `'manual'`, because a typed chord is a statement, not a reading,
 * and must never grow a "possibly…" line; and `chosen` carries the spelling, because
 * somebody who typed D♭m has already answered the question `respell` exists to ask.
 */
export function useManualChordEntry(view: SessionView): (chord: CanonicalChord) => void {
  const { onManualChord, dispatch } = view;
  return useCallback(
    (chord: CanonicalChord) => {
      if (onManualChord) {
        onManualChord(chord);
        return;
      }
      dispatch({
        type: 'CHORD_DETECTED',
        detected: synthesizeDetection(chord, performanceNow()),
        source: 'manual',
        chosen: chord,
      });
    },
    [onManualChord, dispatch],
  );
}

export function ManualEntryPanel({ view }: { view: SessionView }): React.JSX.Element {
  const [typed, setTyped] = useState('');
  const commit = useManualChordEntry(view);

  const submit = (chord: CanonicalChord): void => {
    commit(chord);
    setTyped('');
  };

  return (
    <section className="hc-manual" data-testid="hc-manual-entry">
      <h3 className="hc-manual__title">Type chords instead</h3>
      <p className="hc-manual__blurb">
        Everything else works the same — the graph, the ideas, the sketchbook.
      </p>
      <ChordTextEntry
        value={typed}
        onChange={setTyped}
        onCommit={submit}
        keyContext={view.state.keyEstimate}
        label="Chord"
        placeholder="e.g. Fmaj7"
      />
    </section>
  );
}
